import { useFormik } from 'formik'
import { FC, useMemo } from 'react'
import { useT } from '_metronic/i18n/Metronici18n'
import { addVinculosToModelo, removeVinculosFromModelo } from '../../core/_requests'
import { ModeloView, ModeloVinculoList } from '../../core/_models'
import { modeloVinculosSchema } from './validation/modeloVinculosSchema'
import {
  Button,
  ButtonIcon,
  FetchState,
  FormSelect,
  GroupedList,
  GroupedListGroup,
  IOption,
  ListLoading,
  LoadingIndicator,
  useModal,
} from 'app/components'
import {
  ButtonType,
  IconPaths,
  IconSize,
  OptionsHelper,
  showAlert,
  showConfirmAlert,
  showError,
} from '_metronic/helpers'

export const ModeloVinculosModal: FC = () => {
  const { closeModal, fetchStates, setData } = useModal()
  const { t } = useT()

  const { data: modelo } = (fetchStates['modelo'] || {}) as FetchState<ModeloView>

  const groupedVinculos = useMemo(() => {
    const vinculos = modelo?.vinculos || []
    const map = new Map<number, GroupedListGroup<ModeloVinculoList>>()
    vinculos.forEach((item) => {
      const cidId = item.cid?.id
      if (cidId == null) return
      const group = map.get(cidId) || {
        id: cidId,
        title: `${item.cid?.codigo} - ${item.cid?.descricao}`,
        items: [],
      }
      group.items.push(item)
      map.set(cidId, group)
    })
    return Array.from(map.values())
  }, [modelo?.vinculos])

  const formik = useFormik({
    initialValues: {
      cids: [] as IOption<number>[],
      medicamentos: [] as IOption<number>[],
    },
    validationSchema: useMemo(() => modeloVinculosSchema(t), [t]),
    onSubmit: async (values, { setSubmitting }) => {
      setSubmitting(true)

      try {
        if (!modelo?.id || !values.cids.length || !values.medicamentos.length) return

        const updatedModelo = await addVinculosToModelo(modelo.id, {
          cids: values.cids.map((cid) => Number(cid.value)),
          medicamentos: values.medicamentos.map((med) => Number(med.value)),
        })

        setData('modelo', updatedModelo)

        showAlert({
          title: t('common.success'),
          text: t('alert.create', {
            name: t('vinculo.label'),
            gender: t('vinculo.gender'),
          }),
          icon: 'success',
        })
        formik.resetForm()
      } catch (error) {
        showError(error)
      } finally {
        setSubmitting(false)
      }
    },
  })

  const handleRemoveMedicamento = async (cidId: number, medicamentoId: number) => {
    try {
      const isConfirmed = await showConfirmAlert()
      if (!isConfirmed) return
      const updatedModelo = await removeVinculosFromModelo(modelo?.id as number, {
        cid: cidId,
        medicamentos: [medicamentoId],
      })
      setData('modelo', updatedModelo)
      showAlert({
        title: t('common.success'),
        text: t('alert.delete', {
          name: t('vinculo.label'),
          gender: t('vinculo.gender'),
        }),
        icon: 'success',
      })
    } catch (error) {
      showError(error)
    }
  }

  const handleRemoveAll = async (cidId: number) => {
    try {
      const isConfirmed = await showConfirmAlert()
      if (!isConfirmed) return
      const updatedModelo = await removeVinculosFromModelo(modelo?.id as number, {
        cid: cidId,
        medicamentos: null,
      })
      setData('modelo', updatedModelo)
      showAlert({
        title: t('common.success'),
        text: t('alert.delete', {
          name: t('vinculo.plural'),
          gender: t('vinculo.gender'),
        }),
        icon: 'success',
      })
    } catch (error) {
      showError(error)
    }
  }

  const extraParams = useMemo(() => ({ onlyLme: true }), [])

  return (
    <>
      <GroupedList
        groups={groupedVinculos}
        renderGroupHeader={(group) => (
          <>
            <strong>{group.title}</strong>
            <Button
              className='btn-color-danger p-0'
              size='btn-sm'
              type={ButtonType.Button}
              onClick={() => handleRemoveAll(Number(group.id))}
              disabled={!group.id}
            >
              {t('common.remove.all')}
            </Button>
          </>
        )}
        renderItem={(item, group) => (
          <>
            <span>{item.medicamento?.nome || 'Medicamento desconhecido'}</span>
            <ButtonIcon
              icon={IconPaths.Trash}
              iconSize={IconSize.Small}
              className='m-0 p-0'
              onClick={() => {
                if (group.id && item.medicamento?.id) {
                  handleRemoveMedicamento(Number(group.id), item.medicamento.id)
                }
              }}
              disabled={!group.id || !item.medicamento?.id}
            />
          </>
        )}
        itemKey={(item) => item.id}
        emptyContent={
          <p className='text-muted'>{t('common.none', { name: t('vinculo.plural.lowercase') })}</p>
        }
      />

      <form onSubmit={formik.handleSubmit} noValidate>
        <FormSelect
          formik={formik}
          name='cids'
          fetchOptions={OptionsHelper.getOptionsCids}
          className='mb-3'
          entity='cid'
          isRequired
          isMulti
        />

        <FormSelect
          formik={formik}
          name='medicamentos'
          fetchOptions={OptionsHelper.getOptionsMedicamentos}
          fetchOptionsParams={extraParams}
          className='mb-6'
          entity='medicamento'
          isMulti
          isRequired
        />

        <div className='d-flex justify-content-center'>
          <Button
            type={ButtonType.Reset}
            className='btn-secondary me-3'
            onClick={closeModal}
            disabled={formik.isSubmitting}
          >
            {t('common.cancel')}
          </Button>
          <Button type={ButtonType.Submit} className='btn-primary' disabled={formik.isSubmitting}>
            {formik.isSubmitting ? <LoadingIndicator /> : t('common.save')}
          </Button>
        </div>
        {formik.isSubmitting && <ListLoading />}
      </form>
    </>
  )
}
